import authService from 'ApiService/Auth/AuthService';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import { AuthContext } from 'contexts/AuthContext';
import Redirect from 'pages/AuthProtection/Redirect';
import PrivacyPolicyModal from 'pages/Model/PrivacyPolicyModal';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { RiEyeCloseLine, RiEyeLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';

function Login() {
  const { state, dispatch } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const [userData, setUserData] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [tempUserData, setTempUserData] = useState(null);
  const userRole = state?.user?.role;
  const userHrRole = state?.user?.hrRole;
  const router = useNavigate();

  const handleChange = (event) => {
    setUserData({ ...userData, [event.target.id]: event.target.value.trim() });
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
    setTimeout(() => setShowPassword(false), 2000);
  };

  const handlePrivacyPolicyAcceptance = async () => {
    try {
      setIsLoading(true);
      const response = await authService.acceptPrivacyPolicy({
        userId: tempUserData.userId,
      });

      if (response?.data.success) {
        dispatch({
          type: 'LOGIN',
          payload: response.data.user,
        });
        setShowPrivacyPolicy(false);
        setTempUserData(null);
        router('/student');
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to accept privacy policy'
      );
    } finally {
      setIsLoading(false);
    }
  };
  const handleClosePrivacyPolicy = () => {
    setShowPrivacyPolicy(false);
    setTempUserData(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    const trimmedData = {
      email: userData.email.trim(),
      password: userData.password.trim(),
    };

    if (!trimmedData.email && !trimmedData.password) {
      toast.error('All fields require valid email/phone.');
      setIsLoading(false);
      return;
    }
    if (!trimmedData.email) {
      toast.error('Email is a required field.');
      setIsLoading(false);
      return;
    }
    if (!trimmedData.password) {
      toast.error('Password is a required field.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await authService.userLogin({
        email: userData.email.toLowerCase().trim(),
        password: userData.password.trim(),
      });

      if (response?.data?.success) {
        if (response.data.requiresPrivacyPolicyAcceptance) {
          setTempUserData(response.data.user);
          setShowPrivacyPolicy(true);
          setIsLoading(false);
          return;
        }

        dispatch({
          type: 'LOGIN',
          payload: response?.data?.user,
        });
        setUserData({ email: '', password: '' });

        // Route based on user role
        switch (response.data.user.role) {
          case 'admin':
            router('/admin');
            break;
          case 'student':
            router('/student');
            break;
          case 'account':
            router('/accountant');
            break;
          case 'placement':
            if (
              response.data.user.hrRole === 'headhr' ||
              response.data.user.hrRole === 'jrhr'
            ) {
              router('/headhr');
            } else {
              router('/placement');
            }
            break;
          case 'headhr':
            router('/headhr');
            break;
          case 'jrhr':
            router('/headhr');
            break;
          case 'superadmin':
            router('/superadmin');
            break;
          case 'teacher':
            router('/teacher');
            break;
          default:
            break;
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userRole) {
      switch (userRole) {
        case 'admin':
          router('/admin');
          break;
        case 'student':
          router('/student');
          break;
        case 'teacher':
          router('/teacher');
          break;
        case 'placement':
          if (userHrRole === 'headhr' || userHrRole === 'jrhr') {
            router('/headhr');
          } else {
            router('/placement');
          }
          break;
        case 'headhr':
          router('/headhr');
          break;
        case 'jrhr':
          router('/headhr');
          break;
        case 'superadmin':
          router('/superadmin');
          break;
        case 'account':
          router('/accountant');
          break;
        default:
          router('/');
      }
    }
  }, [userRole, userHrRole, router]);

  return (
    <Redirect>
      {showPrivacyPolicy && (
        <PrivacyPolicyModal
          isOpen={showPrivacyPolicy}
          onClose={handleClosePrivacyPolicy}
          onAccept={handlePrivacyPolicyAcceptance}
          isLoading={isLoading}
          termsAndConditionType={tempUserData?.termsAndConditionType}
        />
      )}

      <Card extra="max-w-[405px] md:max-w-[510px] md:w-[510px] h-max mx-2.5 md:mx-auto mt-[50px] mb-auto py-2.5 px-4 md:!p-[50px] pt-8 md:pt-[50px]">
        <form onSubmit={handleSubmit}>
          <h3 className="mb-[30px] text-center text-4xl font-bold text-gray-900 dark:text-white">
            Awdiz Institute
          </h3>
          <h3 className="mb-[10px] text-2xl font-bold text-gray-900 dark:text-white">
            Sign In to Your Account
          </h3>
          <p className="mb-3 ml-1 text-base text-gray-600">
            Enter your email and password to sign in!
          </p>
          <InputField
            maxLength={100}
            variant="auth"
            extra="mb-3"
            label="Email*"
            placeholder="mail@simmmple.com"
            id="email"
            type="text"
            value={userData.email}
            onChange={handleChange}
          />
          <div className="relative">
            <InputField
              maxLength={30}
              variant="auth"
              extra="mb-3"
              label="Password*"
              placeholder="Min. 8 characters"
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={userData.password}
              onChange={handleChange}
            />
            <button
              type="button"
              className="absolute right-4 top-2/3 -translate-y-1/2 transform text-lg text-navy-700 dark:text-white"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <RiEyeLine /> : <RiEyeCloseLine />}
            </button>
          </div>
          <div className="mt-2 flex items-center justify-between px-2">
            <p
              className="cursor-pointer text-sm font-medium text-brand-500 hover:text-brand-500 dark:text-white"
              onClick={() => router('/auth/forgot-password')}
            >
              Forgot password?
            </p>
          </div>
          <button
            type="submit"
            className={`mt-4 w-full rounded-xl py-3 text-base font-medium text-white transition duration-200 ${isLoading
              ? 'cursor-not-allowed bg-gray-400'
              : 'bg-brand-500 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:bg-brand-200'
              }`}
            disabled={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </Card>
    </Redirect>
  );
}

export default Login;
